/**
 * Jung Kwan Jang Theme JavaScript
 * Main theme functionality
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  document.addEventListener('DOMContentLoaded', function() {
    initScrollAnimations();
    initHeaderScroll();
    initMobileMenu();
    initQuantitySelectors();
    initProductForm();
    initProductCardAddToCart();
    initCartDrawer();
    initQuickView();
    initFilterSidebar();
    initSortSelect();
    initProductThumbnails();
  });

  /**
   * Scroll-based fade-in animations
   */
  function initScrollAnimations() {
    const fadeElements = document.querySelectorAll('.fade-in');

    if (!fadeElements.length) return;

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    fadeElements.forEach(function(el) {
      observer.observe(el);
    });

    // Trigger for elements already in viewport
    setTimeout(function() {
      fadeElements.forEach(function(el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('visible');
        }
      });
    }, 100);
  }

  /**
   * Header shadow on scroll
   */
  function initHeaderScroll() {
    const header = document.querySelector('[data-header]');
    if (!header) return;

    window.addEventListener('scroll', function() {
      if (window.pageYOffset > 10) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    }, { passive: true });
  }

  /**
   * Mobile menu toggle
   */
  function initMobileMenu() {
    const menuBtn = document.querySelector('[data-mobile-menu-toggle]');
    const mobileNav = document.querySelector('[data-mobile-nav]');

    if (!menuBtn || !mobileNav) return;

    menuBtn.addEventListener('click', function() {
      menuBtn.classList.toggle('active');
      mobileNav.classList.toggle('open');
      document.body.classList.toggle('menu-open');
    });

    // Close menu when clicking a link
    mobileNav.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function() {
        menuBtn.classList.remove('active');
        mobileNav.classList.remove('open');
        document.body.classList.remove('menu-open');
      });
    });
  }

  /**
   * Quantity selectors (+ / -)
   */
  function initQuantitySelectors() {
    document.addEventListener('click', function(e) {
      const decreaseBtn = e.target.closest('[data-qty-decrease]');
      const increaseBtn = e.target.closest('[data-qty-increase]');

      if (decreaseBtn) {
        const input = decreaseBtn.parentElement.querySelector('[data-qty-input]');
        if (input && parseInt(input.value) > parseInt(input.min || 1)) {
          input.value = parseInt(input.value) - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (increaseBtn) {
        const input = increaseBtn.parentElement.querySelector('[data-qty-input]');
        if (input) {
          input.value = parseInt(input.value) + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /**
   * Product form - variant selection and add to cart
   */
  function initProductForm() {
    const form = document.querySelector('[data-product-form]');
    if (!form) return;

    const productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;

    const product = JSON.parse(productJson.textContent);
    const variantIdInput = form.querySelector('[data-variant-id]');
    const priceElement = document.querySelector('[data-product-price]');
    const addToCartBtn = form.querySelector('[data-add-to-cart-btn]');

    // Swatch/button click handlers
    document.querySelectorAll('[data-option-value]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        const optionIndex = parseInt(this.dataset.optionIndex);
        const value = this.dataset.optionValue;

        // Update active state
        this.parentElement.querySelectorAll('[data-option-value]').forEach(function(b) {
          b.classList.remove('active');
        });
        this.classList.add('active');

        // Find matching variant
        updateVariant(product, optionIndex, value);
      });
    });

    // Dropdown change handlers
    document.querySelectorAll('[data-option-selector]').forEach(function(select) {
      select.addEventListener('change', function() {
        const optionIndex = parseInt(this.dataset.optionIndex);
        const value = this.value;
        updateVariant(product, optionIndex, value);
      });
    });

    function updateVariant(product, optionIndex, value) {
      // Get current options
      const options = [];
      document.querySelectorAll('[data-option-selector], [data-option-buttons]').forEach(function(el, index) {
        if (el.tagName === 'SELECT') {
          options[index] = el.value;
        } else {
          const activeBtn = el.querySelector('.active');
          options[index] = activeBtn ? activeBtn.dataset.optionValue : '';
        }
      });

      options[optionIndex] = value;

      // Find matching variant
      const variant = product.variants.find(function(v) {
        return v.option1 === options[0] &&
               (options.length < 2 || v.option2 === options[1]) &&
               (options.length < 3 || v.option3 === options[2]);
      });

      if (variant) {
        variantIdInput.value = variant.id;

        // Update price
        if (priceElement) {
          const priceHtml = variant.compare_at_price > variant.price
            ? '<span class="price price--compare">' + formatMoney(variant.compare_at_price) + '</span>' +
              '<span class="price price--current price--sale">' + formatMoney(variant.price) + '</span>'
            : '<span class="price price--current">' + formatMoney(variant.price) + '</span>';
          priceElement.innerHTML = priceHtml;
        }

        // Update add to cart button
        if (addToCartBtn) {
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = window.theme?.strings?.addToCart || 'Add to cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = window.theme?.strings?.soldOut || 'Sold out';
          }
        }

        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    // Add to cart AJAX
    form.addEventListener('submit', function(e) {
      if (e.submitter && e.submitter.name === 'checkout') return;

      e.preventDefault();

      const formData = new FormData(form);
      const data = {
        id: formData.get('id'),
        quantity: formData.get('quantity') || 1
      };

      addToCartBtn.disabled = true;
      addToCartBtn.textContent = window.theme?.strings?.adding || 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })
      .then(function(response) { return response.json(); })
      .then(function(item) {
        addToCartBtn.textContent = window.theme?.strings?.added || 'Added!';
        updateCartCount();
        openCartDrawer();

        setTimeout(function() {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = window.theme?.strings?.addToCart || 'Add to cart';
        }, 2000);
      })
      .catch(function(error) {
        console.error('Error:', error);
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = window.theme?.strings?.addToCart || 'Add to cart';
      });
    });
  }

  /**
   * Product card add-to-cart buttons (homepage, collection pages)
   */
  function initProductCardAddToCart() {
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('.product-card__add-btn');
      if (!btn || btn.disabled) return;

      var variantId = btn.dataset.productId;
      if (!variantId) return;

      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1
        })
      })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      })
      .then(function() {
        btn.textContent = 'Added!';
        updateCartCount();
        showToast('Product added to cart');

        setTimeout(function() {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
      })
      .catch(function() {
        btn.disabled = false;
        btn.textContent = originalText;
        showToast('Could not add to cart', 'error');
      });
    });
  }

  /**
   * Cart drawer
   */
  function initCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    // Open cart drawer
    document.querySelectorAll('.header__cart-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        openCartDrawer();
      });
    });

    // Close cart drawer
    document.querySelectorAll('[data-cart-drawer-close]').forEach(function(btn) {
      btn.addEventListener('click', closeCartDrawer);
    });

    // Close on escape
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        closeCartDrawer();
      }
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    // Fetch fresh cart data and rebuild drawer contents
    refreshCartDrawer().then(function() {
      drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }

  function closeCartDrawer() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer) {
      drawer.classList.remove('open');
      document.body.style.overflow = '';
    }
  }

  function refreshCartDrawer() {
    return fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) {
        var body = document.querySelector('[data-cart-drawer-body]');
        var footer = document.querySelector('.cart-drawer__footer');

        if (!body) return;

        if (cart.item_count === 0) {
          body.innerHTML =
            '<div class="cart-drawer__empty">' +
              '<p>Your cart is empty</p>' +
              '<a href="/collections/all" class="btn btn--primary">Continue Shopping</a>' +
            '</div>';
          if (footer) footer.style.display = 'none';
        } else {
          var itemsHtml = '<div class="cart-drawer__items">';

          cart.items.forEach(function(item) {
            var imageUrl = item.image ? item.image.replace(/(\.\w+)$/, '_120x$1') : '';

            itemsHtml +=
              '<div class="cart-drawer__item" data-cart-item="' + item.key + '">' +
                '<a href="' + item.url + '" class="cart-drawer__item-image">' +
                  (imageUrl ? '<img src="' + imageUrl + '" alt="' + item.title + '" width="60" height="60" loading="lazy">' : '') +
                '</a>' +
                '<div class="cart-drawer__item-details">' +
                  '<a href="' + item.url + '" class="cart-drawer__item-title">' + item.product_title + '</a>' +
                  (item.variant_title ? '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>' : '') +
                  '<div class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</div>' +
                  '<div class="cart-drawer__item-quantity">' +
                    '<button type="button" class="cart-drawer__qty-btn" data-cart-change="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease">-</button>' +
                    '<input type="number" class="cart-drawer__qty-input" value="' + item.quantity + '" min="0" data-qty-input readonly>' +
                    '<button type="button" class="cart-drawer__qty-btn" data-cart-change="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
                  '</div>' +
                '</div>' +
                '<button type="button" class="cart-drawer__item-remove" data-cart-change="' + item.key + '" data-qty="0" aria-label="Remove">' +
                  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
                '</button>' +
              '</div>';
          });

          itemsHtml += '</div>';
          body.innerHTML = itemsHtml;

          // Update or create footer
          var drawerContent = document.querySelector('.cart-drawer__content');
          if (footer) {
            footer.style.display = '';
          } else {
            footer = document.createElement('div');
            footer.className = 'cart-drawer__footer';
            drawerContent.appendChild(footer);
          }

          footer.innerHTML =
            '<div class="cart-drawer__subtotal">' +
              '<span>Subtotal</span>' +
              '<span data-cart-subtotal>' + formatMoney(cart.total_price) + '</span>' +
            '</div>' +
            '<p class="cart-drawer__note">Taxes and shipping calculated at checkout</p>' +
            '<div class="cart-drawer__actions">' +
              '<a href="/cart" class="btn btn--outline btn--full">View Cart</a>' +
              '<a href="/checkout" class="btn btn--primary btn--full">Checkout</a>' +
            '</div>';

          // Bind quantity change buttons
          body.querySelectorAll('[data-cart-change]').forEach(function(btn) {
            btn.addEventListener('click', function() {
              var key = this.dataset.cartChange;
              var qty = parseInt(this.dataset.qty);
              updateCartItem(key, qty);
            });
          });
        }

        // Update cart count
        document.querySelectorAll('[data-cart-count]').forEach(function(el) {
          el.textContent = cart.item_count;
        });
      });
  }

  function updateCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
    .then(function(response) { return response.json(); })
    .then(function() {
      refreshCartDrawer();
    });
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function(el) {
          el.textContent = cart.item_count;
        });
      });
  }

  /**
   * Quick view modal
   */
  function initQuickView() {
    const modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return;

    document.addEventListener('click', function(e) {
      const quickViewBtn = e.target.closest('[data-quick-view]');
      if (quickViewBtn) {
        e.preventDefault();
        const handle = quickViewBtn.dataset.quickView;
        openQuickView(handle);
      }
    });

    document.querySelectorAll('[data-quick-view-close]').forEach(function(btn) {
      btn.addEventListener('click', closeQuickView);
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) {
        closeQuickView();
      }
    });
  }

  function openQuickView(handle) {
    const modal = document.querySelector('[data-quick-view-modal]');
    const body = modal.querySelector('[data-quick-view-body]');

    modal.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Show loading
    body.innerHTML = '<div class="quick-view-modal__loading"><div class="loading-spinner"></div></div>';

    // Fetch product
    fetch('/products/' + handle + '?view=quick-view')
      .then(function(response) { return response.text(); })
      .then(function(html) {
        body.innerHTML = html;
        initQuantitySelectors();
      })
      .catch(function(error) {
        body.innerHTML = '<p>Error loading product</p>';
      });
  }

  function closeQuickView() {
    const modal = document.querySelector('[data-quick-view-modal]');
    if (modal) {
      modal.classList.remove('open');
      document.body.style.overflow = '';
    }
  }

  /**
   * Filter sidebar (mobile)
   */
  function initFilterSidebar() {
    const filterBtn = document.querySelector('[data-filter-toggle]');
    const sidebar = document.querySelector('[data-filters-sidebar]');

    if (!filterBtn || !sidebar) return;

    filterBtn.addEventListener('click', function() {
      sidebar.classList.toggle('open');
      document.body.classList.toggle('filters-open');
    });
  }

  /**
   * Sort select
   */
  function initSortSelect() {
    const sortSelect = document.querySelector('[data-sort-select]');
    if (!sortSelect) return;

    sortSelect.addEventListener('change', function() {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  /**
   * Product thumbnails
   */
  function initProductThumbnails() {
    document.querySelectorAll('[data-thumbnail]').forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        const mainImage = document.querySelector('[data-main-image]');
        const imageUrl = this.dataset.imageUrl;

        if (mainImage && imageUrl) {
          mainImage.src = imageUrl;

          // Update active state
          document.querySelectorAll('[data-thumbnail]').forEach(function(t) {
            t.classList.remove('active');
          });
          this.classList.add('active');
        }
      });
    });
  }

  /**
   * Format money
   */
  function formatMoney(cents) {
    return 'RM ' + (cents / 100).toFixed(2);
  }

  /**
   * Show toast notification
   */
  window.showToast = function(message, type) {
    type = type || 'success';

    const existing = document.querySelector('.toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'toast toast--' + type;
    toast.innerHTML = '<span>' + message + '</span>';

    document.body.appendChild(toast);

    setTimeout(function() {
      toast.style.animation = 'slideOutRight 0.3s ease forwards';
      setTimeout(function() { toast.remove(); }, 300);
    }, 2500);
  };

})();
